import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  postCreateReportV2,
  getReportStatusV2,
  deleteReportV2,
  getReportsV2,
  getReportTypesV2,
} from "@intelligentlilli/api-layer";
// State
import { setLoading } from "../../State/slices/session-slice";
import { setReports } from "../../State/slices/reports-slice";
import { setReportTypes } from "../../State/slices/reportTypes-slice";

export const useReportsHook = (
  server,
  navigate,
  setLocalLoading,
  setLocalError
) => {
  const dispatch = useDispatch();
  const reportTypes = useSelector((state) => state.reportTypes);
  const reports = useSelector((state) => state.reports);
  const [closeConfirmModal, setCloseConfirmModal] = useState(true);

  useEffect(() => {
    setLocalError && setLocalError(false);
    setLocalLoading && setLocalLoading(true);
    async function fetchData() {
      const reportTypesResponse = getReportTypesV2(server, "web");
      const reportsResponse = getReportsV2(server, "web");

      await Promise.all([reportTypesResponse, reportsResponse])
        .then((res) => {
          if (res[0]?.ok && res[1]?.ok) {
            dispatch(setReportTypes(res[0]?.data));
            dispatch(setReports(res[1]?.data));
          } else {
            setLocalError && setLocalError(true);
          }
          setLocalLoading && setLocalLoading(false);
        })
        .catch(() => {
          setLocalLoading && setLocalLoading(false);
          setLocalError && setLocalError(true);
        });
    }
    fetchData();
  }, [navigate, server, dispatch, setLocalLoading, setLocalError]);

  const createReport = (
    {
      serviceUserId,
      reportType,
      reportStartDate,
      reportEndDate,
      entityProperties,
    },
    setLocalLoading,
    setLocalError
  ) => {
    setLocalLoading(true);
    postCreateReportV2(
      server,
      serviceUserId,
      reportType, // name
      reportStartDate,
      reportEndDate, // "YYYY-MM-DD"
      entityProperties,
      "web"
    )
      .then((res) => {
        if (res.ok) {
          // get the report status
          const reportId = res.data.requestID;
          // poll the get reports endpoint until the report is ready
          // Number of times to call the report status endpoint
          let iterations = 40;
          // Set an interval to fetch the status
          const interval = window.setInterval(() => {
            if (iterations > 0) {
              getReportStatusV2(server, reportId, "web").then((res) => {
                if (res.ok) {
                  // If the response comes back with a location we can stop looping and download the report
                  if (res.data.current_stage === "ready") {
                    setLocalLoading(false);
                    window.clearInterval(interval);
                    navigate(`/pdf-previewV2/${res.data.request_id}`);
                  }
                  // Otherwise the report is still in the queue so iterate again and reduce the number by 1
                  else {
                    iterations--;
                  }
                  // Once we get to zero iterations clear the interval stop the spinner and show the error message
                  if (iterations === 0) {
                    window.clearInterval(interval);
                    setLocalLoading(false);
                    setLocalError(true);
                  }
                } else {
                  window.clearInterval(interval);
                }
              });
            }
          }, 2000);
        } else {
          setLocalLoading(false);
          setLocalError(true);
        }
      })
      .catch(() => {
        setLocalLoading(false);
        setLocalError(true);
      });
  };

  const deleteSelectedReport = (report) => {
    dispatch(setLoading(true));
    deleteReportV2(server, report.request_id, "web")
      .then((res) => {
        if (res.ok) {
          // remove the report from the redux store
          dispatch(
            setReports(
              reports.filter((r) => r.request_id !== report.request_id)
            )
          );
        }
        dispatch(setLoading(false));
      })
      .catch(() => {
        dispatch(setLoading(false));
      });
    setCloseConfirmModal(true);
  };

  return {
    createReport,
    deleteSelectedReport,
    reportTypes,
    reports,
    closeConfirmModal,
    setCloseConfirmModal,
  };
};
